import firebase from "firebase/app";
import { ModelData } from "./school_ai_admin";

export const schoolAIStudentAnalysisPromptCollectionKey =
  "studentAnalysisPrompts";
export const journalAnalysisPromptCollectionKey = "journalAnalysisPrompts"; // 現状ひつつのドキュメントの前提だが、複数設定できる設計にしておく

type AIModel = Omit<ModelData, "active">;

export type SchoolAIAnalysisPromptData = {
  buttonTitle: string;
  model: AIModel;
  prompt: string;
  active: boolean;
  description?: string;
};

//以下、student analysisのプロンプトデータ
export type StudentAnalysisPrompt = {
  ref: firebase.firestore.DocumentReference;
  data: StudentAnalysisPromptData;
};

export type StudentAnalysisPromptData = SchoolAIAnalysisPromptData;

export function convertToStudentAnalysisPrompt(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): StudentAnalysisPrompt {
  const _data: StudentAnalysisPromptData = {
    buttonTitle: data.buttonTitle,
    model: data.model,
    prompt: data.prompt,
    active: data.active
  };
  if (data.description) {
    _data.description = data.description;
  }
  return { ref, data: _data };
}

export type JournalAnalysisPrompt = {
  ref: firebase.firestore.DocumentReference;
  data: JournalAnalysisPromptData;
};

export type JournalAnalysisPromptData = {
  prompt: string;
  active: boolean;
  createdAt: number;
};

export function convertToJournalAnalysisPrompt(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): JournalAnalysisPrompt {
  const _data: JournalAnalysisPromptData = {
    prompt: data.prompt,
    active: data.active,
    createdAt: data.createdAt
  };
  return { ref, data: _data };
}

//以下、生徒会話分析ジョブ（バックエンドで非同期実行される分析）のデータ
export const studentAnalysisJobsCollectionKey = "studentAnalysisJobs";

export type StudentAnalysisJobStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";

export type StudentAnalysisJobType = "customPrompt" | "savedPrompt";

export type StudentAnalysisJobProgressStage =
  | "queued"
  | "fetching"
  | "mapping"
  | "reducing"
  | "completed"
  | "failed";

export type StudentAnalysisJobProgress = {
  stage: StudentAnalysisJobProgressStage;
  processedChunks?: number;
  totalChunks?: number;
  conversationCount?: number;
  messageCount?: number;
  retriedCount?: number;
  uncoveredLabels?: string[];
  truncatedConversationLabels?: string[];
};

export type StudentAnalysisJobFilter = {
  promptId?: string | null; // 会話のアプリ（promptRef.id）での絞り込み
  fromCreatedAt?: number | null; // 期間絞り込み（unix秒）
  toCreatedAt?: number | null; // 期間絞り込み（unix秒）
  conversationCount: number;
  messageCount: number;
};

export type StudentAnalysisJob = {
  ref: firebase.firestore.DocumentReference;
  data: StudentAnalysisJobData;
};

export type StudentAnalysisJobData = {
  status: StudentAnalysisJobStatus;
  analysisType: StudentAnalysisJobType;
  analysisPromptId?: string | null;
  analysisTitle?: string | null;
  schoolId: string;
  studentUid: string;
  studentName?: string | null;
  requestedByUid: string;
  promptText: string;
  model: string;
  filter: StudentAnalysisJobFilter;
  progress: StudentAnalysisJobProgress;
  resultText?: string;
  errorMessage?: string;
  errorCode?: string;
  createdAt: number;
  updatedAt: number;
  startedAt?: number | null;
  completedAt?: number | null;
};

export function convertToStudentAnalysisJob(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): StudentAnalysisJob {
  const filterData = data.filter ?? {};
  const progressData = data.progress ?? {};
  const _data: StudentAnalysisJobData = {
    status: ["pending", "running", "completed", "failed"].includes(data.status)
      ? data.status
      : "pending",
    analysisType: ["customPrompt", "savedPrompt"].includes(data.analysisType)
      ? data.analysisType
      : "savedPrompt",
    analysisPromptId:
      typeof data.analysisPromptId === "string" ? data.analysisPromptId : null,
    analysisTitle:
      typeof data.analysisTitle === "string" ? data.analysisTitle : null,
    schoolId: data.schoolId ?? "",
    studentUid: data.studentUid ?? "",
    studentName: typeof data.studentName === "string" ? data.studentName : null,
    requestedByUid: data.requestedByUid ?? "",
    promptText: data.promptText ?? "",
    model: data.model ?? "",
    filter: {
      promptId:
        typeof filterData.promptId === "string" ? filterData.promptId : null,
      fromCreatedAt:
        typeof filterData.fromCreatedAt === "number"
          ? filterData.fromCreatedAt
          : null,
      toCreatedAt:
        typeof filterData.toCreatedAt === "number"
          ? filterData.toCreatedAt
          : null,
      conversationCount:
        typeof filterData.conversationCount === "number"
          ? filterData.conversationCount
          : 0,
      messageCount:
        typeof filterData.messageCount === "number"
          ? filterData.messageCount
          : 0
    },
    progress: {
      stage: progressData.stage ?? "queued",
      processedChunks:
        typeof progressData.processedChunks === "number"
          ? progressData.processedChunks
          : undefined,
      totalChunks:
        typeof progressData.totalChunks === "number"
          ? progressData.totalChunks
          : undefined,
      conversationCount:
        typeof progressData.conversationCount === "number"
          ? progressData.conversationCount
          : undefined,
      messageCount:
        typeof progressData.messageCount === "number"
          ? progressData.messageCount
          : undefined,
      retriedCount:
        typeof progressData.retriedCount === "number"
          ? progressData.retriedCount
          : undefined,
      uncoveredLabels: Array.isArray(progressData.uncoveredLabels)
        ? progressData.uncoveredLabels.filter(
            (label: unknown) => typeof label === "string"
          )
        : [],
      truncatedConversationLabels: Array.isArray(
        progressData.truncatedConversationLabels
      )
        ? progressData.truncatedConversationLabels.filter(
            (label: unknown) => typeof label === "string"
          )
        : []
    },
    createdAt: data.createdAt ?? 0,
    updatedAt: data.updatedAt ?? 0,
    startedAt: typeof data.startedAt === "number" ? data.startedAt : null,
    completedAt: typeof data.completedAt === "number" ? data.completedAt : null
  };
  if (typeof data.resultText === "string") {
    _data.resultText = data.resultText;
  }
  if (typeof data.errorMessage === "string") {
    _data.errorMessage = data.errorMessage;
  }
  if (typeof data.errorCode === "string") {
    _data.errorCode = data.errorCode;
  }
  return { ref, data: _data };
}
